import axios from 'axios';

// API Endpoints
const GEO_LOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Get user's geolocation information (country and IP)
 */
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(GEO_LOCATION_API, {
      headers: {
        'accept': 'text/plain'
      },
      timeout: 10000 // 10 second timeout
    });

    console.log('GeoLocation API response:', response.data);

    // Check if API response is successful
    if (response.data && response.data.isSuccess && response.data.statusCode === 200 && response.data.data) {
      return {
        success: true,
        data: {
          ipAddress: response.data.data.ipAddress || '0.0.0.0',
          country: response.data.data.countryCode || 'US',
          countryName: response.data.data.countryName || 'United States',
          countryCode: response.data.data.countryCode || 'US',
          regionName: response.data.data.regionName || '',
          cityName: response.data.data.cityName || '',
          timeZone: response.data.data.timeZone || '',
          latitude: response.data.data.latitude || '',
          longitude: response.data.data.longitude || '',
          zipCode: response.data.data.zipCode || '',
          asn: response.data.data.asn || '',
          as: response.data.data.as || '',
          isProxy: response.data.data.isProxy || false,
          validationMode: response.data.data.validationMode || '',
          verificationDate: response.data.data.verificationDate || '',
          headerIpAddress: response.data.data.headerIpAddress || null
        },
        fullResponse: response.data // Store full response for geoLocation field
      };
    } else {
      console.warn('Geolocation API returned unsuccessful response:', response.data?.errorMessage);
      // Return default US location
      return {
        success: false,
        data: {
          ipAddress: '0.0.0.0',
          country: 'US',
          countryName: 'United States',
          countryCode: 'US',
          regionName: '',
          cityName: ''
        },
        fullResponse: null
      };
    }
  } catch (error) {
    console.warn('Error fetching geo location (using fallback):', error.message);
    // Return default US location as fallback
    return {
      success: false,
      data: {
        ipAddress: '0.0.0.0',
        country: 'US',
        countryName: 'United States',
        countryCode: 'US',
        regionName: '',
        cityName: ''
      },
      fullResponse: null
    };
  }
};

/**
 * Submit lead to CRM
 * @param {Object} leadData - Lead information
 * @param {string} leadData.name - Full name
 * @param {string} leadData.email - Email address
 * @param {number} leadData.countryDialCode - Country dial code (numeric)
 * @param {string} leadData.country - Country code (e.g., 'US', 'NL')
 * @param {string} leadData.phoneNumber - Phone number (only digits, no prefix)
 * @param {string} leadData.ipAddress - IP address from geo location
 * @param {string} leadData.description - Message/description
 * @param {string} leadData.pageUrl - Current page URL
 * @param {Object} leadData.geoLocationData - Full geolocation response (optional)
 * @param {string} leadData.packageTitle - Package title (optional)
 * @param {number} leadData.packageAmount - Package amount (optional)
 */
export const submitLead = async (leadData) => {
  try {
    // Ensure phone number is only digits (remove any non-numeric characters)
    const cleanPhoneNumber = leadData.phoneNumber.replace(/\D/g, '');

    // Build the payload with required and optional fields
    const payload = {
      name: leadData.name,
      email: leadData.email,
      countryDialCode: parseInt(leadData.countryDialCode),
      country: leadData.country,
      phoneNumber: cleanPhoneNumber,
      ipAddress: leadData.ipAddress,
      description: leadData.description || '',
      pageUrl: leadData.pageUrl || window.location.href,
      city: leadData.geoLocationData?.cityName || '',
      state: leadData.geoLocationData?.regionName || '',
      geoLocation: leadData.geoLocationData ? JSON.stringify(leadData.geoLocationData) : '',
      packageTitle: leadData.packageTitle || '',
      packageAmount: leadData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await axios.post(LEAD_API, payload, {
      headers: {
        'Content-Type': 'application/json',
      }
    });

    console.log('Lead API response:', response.data);

    // Check if response exists and has data
    if (!response || !response.data) {
      return {
        success: false,
        message: 'Invalid response from server. Please try again.'
      };
    }

    // Check if response is successful
    if (response.data.statusCode === 200 && response.data.isSuccess) {
      return {
        success: true,
        customerId: response.data.data?.customerId || null,
        message: response.data.data?.message || 'Lead submitted successfully'
      };
    } else {
      // Handle non-200 responses
      if(response.data.statusCode === 401 && !response.data.isSuccess) {
          return {
            success: false,
            message:  response.data.message
        };
      }
      else {
        return {
          success: false,
          message:  response.data.errorMessage || response.data.data?.message || 'Failed to submit lead. Please try again.'
        };
      }      
    }

  } catch (error) {
    console.error('Error submitting lead:', error);

    // Handle API error responses
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      
      // Check for 400 or 401 errors
      if (errorData.statusCode === 400 || errorData.statusCode === 401) {
        return {
          success: false,
          message: errorData.errorMessage || errorData.data?.message || 'Validation error. Please check your input.'
        };
      }

      return {
        success: false,
        message: errorData.errorMessage || errorData.data?.message || 'Failed to submit lead. Please try again.'
      };
    }

    // Handle network errors or other issues
    return {
      success: false,
      message: error.message || 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Helper function to get country dial code from country code
 */
export const getDialCodeFromCountry = (countryCode) => {
  const countryDialCodes = {
    'US': 1, 'CA': 1, 'GB': 44, 'AU': 61, 'NZ': 64, 'IN': 91,
    'PK': 92, 'BD': 880, 'NL': 31, 'DE': 49, 'FR': 33, 'ES': 34,
    'IT': 39, 'PT': 351, 'BE': 32, 'CH': 41, 'AT': 43, 'SE': 46,
    'NO': 47, 'DK': 45, 'FI': 358, 'PL': 48, 'CZ': 420, 'HU': 36,
    'RO': 40, 'BG': 359, 'GR': 30, 'TR': 90, 'RU': 7, 'UA': 380,
    'IL': 972, 'SA': 966, 'AE': 971, 'QA': 974, 'KW': 965, 'OM': 968,
    'EG': 20, 'ZA': 27, 'KE': 254, 'NG': 234, 'GH': 233, 'CN': 86,
    'JP': 81, 'KR': 82, 'TH': 66, 'VN': 84, 'MY': 60, 'SG': 65,
    'PH': 63, 'ID': 62, 'BR': 55, 'MX': 52, 'AR': 54, 'CL': 56,
    'CO': 57, 'PE': 51, 'VE': 58
  };
  
  return countryDialCodes[countryCode] || 1;
};

/**
 * Navigate to application page with customer ID
 */
export const navigateToApplication = (customerId) => {
  window.location.href = `/application/main?customerid=${customerId}`;
};
